import React, { useMemo } from "react"
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@f2e/ui/components/base"
import { Agent, AgentType } from "@/types"
import { useTranslation } from "react-i18next"

interface AgentSelectProps {
  agents: Agent[]
  currentWorkspaceId?: string
  defaultValue?: string
  onAgentChange: (agentId: string) => void
  compact?: boolean
  isLoading?: boolean
  hasError?: boolean
  onRetry?: () => void
}

const AgentSelect: React.FC<AgentSelectProps> = ({
  agents,
  currentWorkspaceId,
  defaultValue = "all",
  onAgentChange,
  compact = false,
  isLoading = false,
  hasError = false,
  onRetry,
}) => {
  const { t } = useTranslation()

  // Memoize agent filtering logic to prevent unnecessary recalculations
  const { basicAgents } = useMemo(() => {
    const filtered =
      currentWorkspaceId &&
      currentWorkspaceId !== "all" &&
      currentWorkspaceId !== ""
        ? agents.filter((agent) => agent.workspaceId === currentWorkspaceId)
        : agents

    return {
      basicAgents: filtered.filter((agent) => agent.type === AgentType.Basic),
    }
  }, [agents, currentWorkspaceId])

  const triggerId = "agent-select"
  return (
    <div
      className={compact ? "grid gap-1.5" : "mb-4 flex items-center space-x-4"}
    >
      <label
        htmlFor={triggerId}
        className={
          compact ? "text-xs font-medium text-muted-foreground" : "font-medium"
        }
      >
        {t("historyList.agentSelect.label")}
      </label>
      <div
        className={
          compact ? "flex items-center gap-2" : "flex items-center space-x-2"
        }
      >
        <Select
          onValueChange={onAgentChange}
          defaultValue={defaultValue}
          disabled={isLoading || hasError}
        >
          <SelectTrigger
            id={triggerId}
            className={compact ? "h-9 w-full min-w-0 text-sm" : "w-[220px]"}
          >
            <SelectValue
              placeholder={
                hasError
                  ? "Failed to load agents"
                  : isLoading
                    ? "Loading agents..."
                    : t("historyList.agentSelect.placeholder")
              }
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" className="pl-6">
              {t("historyList.agentSelect.none")}
            </SelectItem>
            <SelectItem value="all" className="pl-6">
              {t("historyList.agentSelect.all")}
            </SelectItem>

            {basicAgents.length > 0 &&
              basicAgents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id} className="pl-6">
                  {agent.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        {hasError && onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            className="text-sm"
          >
            {t("historyList.agentSelect.retry")}
          </Button>
        )}
      </div>
    </div>
  )
}

export default React.memo(AgentSelect)
